import { CountryCode } from '../types/apollo/generated/types.generated';

export interface CountryCodeAndName {
  code: CountryCode;
  name: string;
  /**
   * Long form name that can be used to describe the country.
   *
   * Example: "Ships only in [The United States | Canada | The United Kingdom]"
   */
  longformName: string;
  /**
   * Short form name that can be used to describe the country in labels.
   *
   * Example: "[U.S. | U.K.] Only"
   */
  shortformName?: string;
  locale: string;
}

const US_COUNTRY_CODE = {
  code: CountryCode.Us,
  name: 'United States',
  longformName: 'the United States',
  shortformName: 'U.S.',
  locale: 'en-US',
};

const EU_COUNTRY_CODES = new Set([
  CountryCode.Fr,
  CountryCode.It,
  CountryCode.De,
  CountryCode.Es,
  CountryCode.Nl,
]);

export const COUNTRY_CODE_AND_NAME: CountryCodeAndName[] = [
  { code: CountryCode.Au, name: 'Australia', longformName: 'Australia', locale: 'en-AU' },
  { code: CountryCode.Ca, name: 'Canada', longformName: 'Canada', locale: 'en-CA' },
  {
    code: CountryCode.Gb,
    name: 'United Kingdom',
    longformName: 'the United Kingdom',
    shortformName: 'U.K.',
    locale: 'en-GB',
  },
  {
    code: CountryCode.Fr,
    name: 'France',
    longformName: 'France',
    locale: 'fr-FR',
  },
  {
    code: CountryCode.It,
    name: 'Italy',
    longformName: 'Italy',
    locale: 'it-IT',
  },
  {
    code: CountryCode.De,
    name: 'Germany',
    longformName: 'Germany',
    locale: 'de-DE',
  },
  {
    code: CountryCode.Es,
    name: 'Spain',
    longformName: 'Spain',
    locale: 'es-ES',
  },
  {
    code: CountryCode.Nl,
    name: 'Netherlands',
    longformName: 'the Netherlands',
    locale: 'nl-NL',
  },
  ...[US_COUNTRY_CODE],
];

export const getCountryObjectFromCode = (
  countryCode: CountryCode
): CountryCodeAndName | undefined =>
  COUNTRY_CODE_AND_NAME.find((country) => country.code === countryCode);

// Defaults to US if country code cannot be found.
export const getCountryObjectFromCodeWithDefault = (countryCode: CountryCode): CountryCodeAndName =>
  getCountryObjectFromCode(countryCode) || US_COUNTRY_CODE;

// Defaults to US (en) if locale cannot be found.
export const getCountryObjectFromLocale = (locale: string): CountryCodeAndName =>
  COUNTRY_CODE_AND_NAME.find((country) => country.locale.toLowerCase() === locale.toLowerCase()) ||
  US_COUNTRY_CODE;

export const isEUCountry = (countryCode: CountryCode | string): boolean =>
  EU_COUNTRY_CODES.has(countryCode as CountryCode);

export const getEUCountries = (): Set<CountryCode> => EU_COUNTRY_CODES;
